import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import { getAllLedgerEntries, LedgerEntry } from '../../services/MasterAdmin/accountantService';

const ProfitLossLedgerPage: React.FC = () => {
  const navigate = useNavigate();
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  const fetchLedgerEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAllLedgerEntries({
        search: searchTerm.trim() || undefined,
        page: currentPage,
        limit: 50,
      });

      // Group by order/serviceRef and aggregate financial fields across all ledger rows.
      const orderMap = new Map<string, LedgerEntry>();
      response.data.ledgerEntries.forEach((entry) => {
        const orderId = entry.serviceReferenceId;
        if (!orderId) return;
        const existing = orderMap.get(orderId);
        if (!existing) {
          orderMap.set(orderId, {
            ...entry,
            commissionAmount: Number(entry.commissionAmount || 0),
            gatewayFeeRecovered: Number(entry.gatewayFeeRecovered || 0),
            internalCompensationAmount: Number(entry.internalCompensationAmount || 0),
          });
        } else {
          existing.commissionAmount =
            Number(existing.commissionAmount || 0) + Number(entry.commissionAmount || 0);
          existing.gatewayFeeRecovered =
            Number(existing.gatewayFeeRecovered || 0) + Number(entry.gatewayFeeRecovered || 0);
          existing.internalCompensationAmount =
            Number(existing.internalCompensationAmount || 0) +
            Number(entry.internalCompensationAmount || 0);
          // Keep latest service completion timestamp for display.
          const existingDate = existing.serviceCompletedAt
            ? new Date(existing.serviceCompletedAt).getTime()
            : 0;
          const nextDate = entry.serviceCompletedAt
            ? new Date(entry.serviceCompletedAt).getTime()
            : 0;
          if (nextDate > existingDate) {
            existing.serviceCompletedAt = entry.serviceCompletedAt;
          }
        }
      });

      setLedgerEntries(Array.from(orderMap.values()));
      setTotalPages(response.pagination.totalPages);
      setTotalEntries(response.pagination.total);
    } catch (err: any) {
      console.error('Error fetching ledger entries:', err);
      setError(err.response?.data?.message || 'Failed to fetch ledger entries. Please try again.');
      setLedgerEntries([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchLedgerEntries();
  }, [fetchLedgerEntries]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        <div>
          <h3 className="font-semibold text-red-800 dark:text-red-400">Error</h3>
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profit and Loss Ledger</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {totalEntries} orders found
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {ledgerEntries.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No orders found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Service Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Commission
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Gateway Charges Recovered
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Platform Compensation Credit
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Net Profit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {ledgerEntries.map((entry) => {
                  const commission = entry.commissionAmount || 0;
                  const gatewayRecovered = entry.gatewayFeeRecovered || 0;
                  const internalCompensation = entry.internalCompensationAmount || 0;
                  // Gateway recovered is loss recovery and is tracked separately.
                  // Net profit includes commission plus platform-side compensation credit.
                  const netProfit = commission + internalCompensation;
                  
                  return (
                    <tr
                      key={entry.ledgerId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {entry.serviceReferenceId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          {entry.serviceType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(commission)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                          {formatCurrency(gatewayRecovered)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                          {formatCurrency(internalCompensation)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`text-sm font-bold ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(netProfit)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(entry.serviceCompletedAt || undefined)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => navigate(`/accountant/ledger/order/${entry.serviceReferenceId}?ledgerType=profit-loss`)}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                      >
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfitLossLedgerPage;
